import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  ColumnHeader,
  DocSignService,
  EgibObject,
  FormAlertService,
  LawPersonControlName,
  LawPersonType,
  MapAction,
  MapId,
  MapObject,
  MapObjectApiType,
  MapObjectTableActionType,
  MapObjectTableState,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  PortalId,
  PzService,
  StreetService,
  ToolType,
  markFormGroupsTouched,
} from '@gk/gk-modules';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  catchError,
  from,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { RequestInProgress } from '../../../guards/request-in-progress/request-in-progress.guard';
import { NewRequestHelperService } from '../../../services/new-request-helper/new-request-helper.service';
import { PlaceService } from '../../../services/place/place.service';
import {
  BsMessageType,
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from '../../../services/request-workspace-state/request-workspace-state.model';
import { BaseNewRequestComponent } from '../../../shared/base-new-request/base-new-request.component';
import { InvestorDetailsComponent } from '../../../shared/investor-details/investor-details.component';
import { RequestReplyFormComponent } from '../../../shared/request-reply-form/request-reply-form.component';
import {
  LBRDUUERNewDataUpdateRequestControlName,
  LBRDUUERNewDataUpdateRequestFormGroup,
} from '../services/new-data-update-request-form/new-data-update-request-form.model';
import { LBRDUUERNewDataUpdateRequestFormService } from '../services/new-data-update-request-form/new-data-update-request-form.service';
import {
  LBRDUUERNewDataUpdateRequest,
  LBRDUUERNewDataUpdateRequestDto,
  LBRDUUERNewDataUpdateRequestFormWorkspaceState,
} from '../services/new-data-update-request/new-data-update-request.model';
import { MainRoutes } from './../../../guards/guard.models';

@Component({
  selector: 'app-lbrc-new-data-update-request',
  templateUrl: './new-data-update-request.component.html',
  standalone: false,
})
export class LBRDUUERNewDataUpdateRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy, RequestInProgress
{
  override controlName = LBRDUUERNewDataUpdateRequestControlName;
  override formGroup: FormGroup<LBRDUUERNewDataUpdateRequestFormGroup>;
  override portalId =
    PortalId.LandAndBuildingRegisterDataUpdateUponEntitiesRequest;
  mapObjectTableState = new MapObjectTableState(
    undefined,
    undefined,
    this.defaultMapGeometryStyles,
    true,
    false,
    true,
    '25',
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    [MapObjectApiType.LandParcel, MapObjectApiType.Building],
    true,
  );
  mapSettings: MapSettings;
  @ViewChild(InvestorDetailsComponent)
  investorDetailsComponent: InvestorDetailsComponent;
  @ViewChild('canDeactivateModal') canDeactivateModal: NgbModalRef;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  successSigned = false;
  workspaceStateDraftVersion =
    WorkspaceStateDraftVersion.LandAndBuildingRegisterDataUpdateUponEntitiesRequestFormVersion;
  workspaceStateId =
    WorkspaceStateId.LandAndBuildingRegisterDataUpdateUponEntitiesRequestForm;

  constructor(
    public override mapStateService: MapStateService,
    public override mapSettingsService: MapSettingsService,
    public override formAlertService: FormAlertService,
    public override placeService: PlaceService,
    public override translateService: TranslateService,
    public override docSignService: DocSignService,
    public override newRequestHelperService: NewRequestHelperService,
    private lbrduuerNewDataUpdateRequestFormService: LBRDUUERNewDataUpdateRequestFormService,
    public override streetService: StreetService,
    private toastr: ToastrService,
    public override router: Router,
    private modalService: NgbModal,
    public override pzService: PzService,
  ) {
    super(
      pzService,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
      formAlertService,
      placeService,
      streetService,
    );
  }

  override ngOnInit(): void {
    this.fetchWrongFileText();
    this.subscribeToDocSignTranslations();
    this.createForm();
    this.fetchMapSettings();
    this.setEntityInEgibColumnVisibilityOnPersonTypeChange();
  }

  createForm(): void {
    this.formGroup =
      this.lbrduuerNewDataUpdateRequestFormService.getFormGroup();
  }

  fetchMapSettings(): void {
    this.mapSettingsService
      .getMapSettings(MapPortalName.AddressNumberRequest)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((mapSettings) => {
        this.mapSettings = mapSettings;
        this.setMapState();
      });
  }

  override setMapState(): void {
    this.mapState = new MapState(
      MapId.LandAndBuildingRegisterDataUpdateUponEntitiesRequest,
      this.mapStateService.getViewState(
        MapId.LandAndBuildingRegisterDataUpdateUponEntitiesRequest,
        this.mapSettings,
        undefined,
        '/api/interesant/wniosek/aktualizacjaDanychEgibPrzezPodmiotWlad/formawladania',
      ),
      this.mapStateService.getToolbarState(
        [ToolType.LandParcel, ToolType.Building],
        this.mapSettings.papers,
        undefined,
        undefined,
        true,
      ),
      this.initialToolsState,
      this.mapStateService.getLayersState(
        MapId.LandAndBuildingRegisterDataUpdateUponEntitiesRequest,
        this.mapSettings,
        MapPortalName.Applicant,
      ),
      [this.mapObjectTableState],
    );
    this.setEntityInEgibColumnVisibilityInitially();
  }

  canDeactivate(): Observable<boolean> | boolean {
    return this.isRequestDirty() && !this.successSigned
      ? from(
          this.modalService.open(this.canDeactivateModal, {
            beforeDismiss: () => false,
          }).result,
        )
      : true;
  }

  isRequestDirty(): boolean {
    return (
      this.formGroup.dirty || this.attachmentsComponent.areChosenDocuments()
    );
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid &&
      this.attachmentsComponent.areDocumentsValid() &&
      this.areMapObjectsChosen()
    );
  }

  handleSubmit(): void {
    this.submitted = true;
    markFormGroupsTouched(this.getInvestorDetailsFormGroup());
    if (!this.isRequestValid()) {
      return;
    }

    this.sendRequest();
  }

  sendRequest(): void {
    this.getRequestBody()
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap((body) =>
          this.docSignService.addToSign(
            body,
            '/api/interesant/wniosek/aktualizacjaDanychEgibPrzezPodmiotWlad/addToSign',
          ),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: (error) => this.handleSendAndValidateFailure(error),
      });
  }

  getRequestBody = (): Observable<LBRDUUERNewDataUpdateRequestDto> => {
    return from(
      Promise.all(this.attachmentsComponent.getConvertedFiles()).catch(
        (error) => new Error(error),
      ),
    ).pipe(
      switchMap((apiFiles) => {
        const apiFilesArray = Object.values(apiFiles).flatMap((file) => file);
        if (
          (Array.isArray(apiFilesArray) &&
            apiFilesArray.some((file) => file instanceof Error)) ||
          apiFiles instanceof Error
        ) {
          this.errorSubmitMessage = this.wrongFilesErrorText;
          this.toastr.error(this.wrongFilesErrorText);

          return throwError(() => new Error(this.wrongFilesErrorText));
        }

        return of(apiFiles);
      }),
      map((apiFiles) =>
        LBRDUUERNewDataUpdateRequest.fromAppToApi(
          this.getFormValue(),
          this.getLandParcels(),
          this.getBuildings(),
          apiFiles as ApiNewDokumentPowiazanyDalDto[],
        ),
      ),
      catchError((err) => {
        console.error(err);

        return throwError(() => err);
      }),
    );
  };

  getWorkspaceStateToSave =
    (): LBRDUUERNewDataUpdateRequestFormWorkspaceState => ({
      formValue: this.getFormValue(),
      parcels: this.getLandParcels(),
      buildings: this.getBuildings(),
    });

  handleLoadWorkspaceResponse(
    state: LBRDUUERNewDataUpdateRequestFormWorkspaceState,
  ): void {
    this.formGroup.patchValue({
      ...state.formValue,
      investorDetails: {
        ...state.formValue.investorDetails,
        addressSelect: undefined,
      },
    });
    if (state.formValue?.investorDetails?.addressSelect) {
      this.investorDetailsComponent.setAddressSelectFormGroupValue(
        state.formValue.investorDetails.addressSelect,
      );
    }
    this.requestReplyFormComponent.patchRequestReplyFormControls({
      documentationForm: state.formValue.documentationForm,
      shippingForm: state.formValue.shippingForm,
    });
    this.handleMapAction(
      new MapAction(MapObjectTableActionType.AddNew, [...state.parcels]),
    );
    this.handleMapAction(
      new MapAction(MapObjectTableActionType.AddToExisting, [
        ...state.buildings,
      ]),
    );
  }

  getLandParcels(): EgibObject[] {
    return [...this.mapState.mapObjectTablesState[0].mapObjects].filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.LandParcel)
      ),
    );
  }

  getBuildings(): EgibObject[] {
    return [...this.mapState.mapObjectTablesState[0].mapObjects].filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.Building)
      ),
    );
  }

  getFormValue(): LBRDUUERNewDataUpdateRequest {
    return this.formGroup.getRawValue();
  }

  override handleDocSignSuccess(): void {
    this.successSigned = true;
    this.docSignUrl = '';
    this.router.navigateByUrl(
      `/${MainRoutes.LandAndBuildingRegisterDataUpdateUponEntitiesRequest}/requests-list`,
    );
  }

  isChangeScopeFormControlValid(): boolean {
    return this.getChangeScopeFormControl().valid;
  }

  getChangeScopeFormControl(): AbstractControl<string> {
    return this.formGroup.get(this.controlName.ChangeScope);
  }

  areMapObjectsChosen(): boolean {
    return !!this.mapState.mapObjectTablesState[0].mapObjects?.length;
  }

  setEntityInEgibColumnVisibilityOnPersonTypeChange(): void {
    this.getInvestorDetailsFormGroup()
      .get(LawPersonControlName.Type)
      .valueChanges.pipe(takeWhile(() => this.isAlive))
      .subscribe((lawPersonType: LawPersonType) => {
        this.setColumnHeaderConfig(lawPersonType === LawPersonType.Natural);
      });
  }

  setEntityInEgibColumnVisibilityInitially(): void {
    const lawPersonType: LawPersonType = this.getInvestorDetailsFormGroup().get(
      LawPersonControlName.Type,
    ).value;
    this.setColumnHeaderConfig(lawPersonType === LawPersonType.Natural);
  }

  setColumnHeaderConfig(entityInEgibDescriptionVisibility: boolean): void {
    this.handleMapAction(
      new MapAction(
        MapObjectTableActionType.ChangeColumnHeader,
        this.getColumnHeaderConfig(entityInEgibDescriptionVisibility),
      ),
    );
  }

  getColumnHeaderConfig(
    entityInEgibDescriptionVisibility: boolean,
  ): ColumnHeader[] {
    return [
      new ColumnHeader('districtName', 'GK.MAP.DISTRICT'),
      new ColumnHeader('mapObjectNumber', 'GK.MAP.PARCEL_OR_BUILDING_NUMBER'),
      new ColumnHeader('typeName', 'GK.MAP.TYPE'),
      new ColumnHeader(
        'entityInEgibDescription',
        'GK.MAP.ENTITY_IN_EGIB',
        entityInEgibDescriptionVisibility,
      ),
    ];
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
